import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { DateInput } from '@framework/form/inputs/DateInput.tsx';
import { NumberInput } from '@framework/form/inputs/NumberInput.tsx';
import { RadioGroupInput } from '@framework/form/inputs/RadioGroupInput.tsx';
import { RadioOptionInput } from '@framework/form/inputs/RadioOptionInput.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InputLikeText } from '../../input/InputLikeText.tsx';
import { InputLikeWrapper } from '../../input/InputLikeWrapper.tsx';
import {
  type SearchableField,
  SearchableFieldType,
} from '../ISearchableField.tsx';
import { AdvancedSearchFormItem } from './AdvancedSearchFormItem.tsx';
import { AdvancedSearchMultipleItemWrapper } from './AdvancedSearchMultipleItemWrapper.tsx';

export interface IAdvancedSearchFieldProps {
  fields: SearchableField[];
  field: SearchableField | null | undefined;
  index: number;
}

export function AdvancedSearchField(props: IAdvancedSearchFieldProps) {
  const { index, fields, field } = props;
  const { t } = useTranslation();

  const options = useMemo(() => {
    if (!field || !('options' in field)) return;
    return field.options.map(({ value, label }) => ({
      value: value.toString(),
      label,
    }));
  }, [field]);

  const valueToString = useCallback((value: any) => value?.toString(), []);

  const datePlaceholderFallback = t(
    'search.component.advanced-search-field.date.placeholder-fallback',
    'GG/MM/AAAA',
  );

  if (!field) {
    console.warn(`[AdvancedSearchField] Field not found at index ${index}`);
    return null;
  }

  switch (field.type) {
    case SearchableFieldType.Number:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <NumberInput
            // data-focusid={field.name}
            size="middle"
            name={field.name}
            placeholder={field.placeholder}
            allowClear
          />
        </AdvancedSearchFormItem>
      );

    case SearchableFieldType.Date:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <DateInput
            size="middle"
            name={field.name}
            type="date"
            placement="bottomLeft"
            placeholder={field.placeholder ?? datePlaceholderFallback}
            allowClear
          />
        </AdvancedSearchFormItem>
      );

    case SearchableFieldType.DateRange:
      return (
        <AdvancedSearchMultipleItemWrapper block>
          <InputLikeText text={'Dal'} left right fixedHeight />
          <AdvancedSearchFormItem
            name={field.from.name}
            label={field.from.label}
          >
            <DateInput
              size="middle"
              name={field.from.name}
              type="date"
              placement="bottomLeft"
              placeholder={field.from.placeholder ?? datePlaceholderFallback}
              allowClear
            />
          </AdvancedSearchFormItem>
          <InputLikeText text={'Fino al'} left right fixedHeight />
          <AdvancedSearchFormItem name={field.to.name} label={field.to.label}>
            <DateInput
              size="middle"
              name={field.to.name}
              type="date"
              placement="bottomLeft"
              placeholder={field.to.placeholder ?? datePlaceholderFallback}
              allowClear
            />
          </AdvancedSearchFormItem>
        </AdvancedSearchMultipleItemWrapper>
      );

    case SearchableFieldType.NumberRange:
      return (
        <AdvancedSearchMultipleItemWrapper block>
          <InputLikeText text={'Da'} left right />
          <AdvancedSearchFormItem
            name={field.from.name}
            label={field.from.label}
          >
            <NumberInput
              size="middle"
              name={field.from.name}
              placeholder={field.from.placeholder}
              allowClear
            />
          </AdvancedSearchFormItem>
          <InputLikeText text={'A'} left right />
          <AdvancedSearchFormItem name={field.to.name} label={field.to.label}>
            <NumberInput
              size="middle"
              name={field.to.name}
              placeholder={field.to.placeholder}
              allowClear
            />
          </AdvancedSearchFormItem>
        </AdvancedSearchMultipleItemWrapper>
      );

    case SearchableFieldType.Select:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <SelectInput
            size="middle"
            name={field.name}
            placeholder={field.placeholder}
            options={options}
            allowClear
          />
        </AdvancedSearchFormItem>
      );

    case SearchableFieldType.Radio:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <InputLikeWrapper left right>
            <RadioGroupInput
              size="large"
              name={field.name}
              style={{ marginTop: '-10px' }}
              transformValue={valueToString}
            >
              {/* Usiamo le radioOptionInput per permettere la de-selezione */}
              {options?.map(option => (
                <RadioOptionInput
                  key={option.value}
                  name={field.name}
                  optionValue={option.value}
                  allowUnselect
                  // TODO: refactor per utitlizzare prop definito in RadioGroupInput
                  transformValue={valueToString}
                >
                  {option.label}
                </RadioOptionInput>
              ))}
            </RadioGroupInput>
          </InputLikeWrapper>
        </AdvancedSearchFormItem>
      );

    // TODO: Deprecata, da rimuovere
    case SearchableFieldType.Switch:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <InputLikeWrapper right left>
            <SwitchInput
              size="default"
              name={field.name}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              style={{ marginTop: '-3px' }}
            />
          </InputLikeWrapper>
        </AdvancedSearchFormItem>
      );

    case SearchableFieldType.Custom:
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          {field.input}
        </AdvancedSearchFormItem>
      );

    case SearchableFieldType.Text:
    default:
      if (field.type !== SearchableFieldType.Text) {
        console.warn('Tipo di campo non supportato', { field });
      }
      return (
        <AdvancedSearchFormItem name={field.name} label={field.label}>
          <TextInput
            // data-focusid={field.name}
            size="middle"
            name={field.name}
            placeholder={field.placeholder}
            allowClear
          />
        </AdvancedSearchFormItem>
      );
  }
}
